import type { ReactElement, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { IconButton } from './IconButton'

export type ToastVariant = 'success' | 'error' | 'info'

export interface ToastOptions {
  title: string
  description?: string
  variant?: ToastVariant
}

export interface ToastContextValue {
  toast: (options: ToastOptions) => void
}

interface ToastRecord extends ToastOptions {
  id: number
}

export interface ToastProviderProps {
  children: ReactNode
}

const ToastContext = createContext<ToastContextValue | null>(null)

/** Errors linger — they usually carry a message the user has to read. */
const DURATIONS: Record<ToastVariant, number> = {
  success: 5000,
  info: 5000,
  error: 8000
}

const MAX_VISIBLE = 4

const variants: Record<ToastVariant, { shell: string; icon: string; node: ReactElement }> = {
  success: {
    shell: 'border-ok-500/35 bg-surface',
    icon: 'bg-ok-100 text-ok-700 dark:bg-ok-950 dark:text-ok-300',
    node: <CircleCheck />
  },
  error: {
    shell: 'border-bad-500/40 bg-surface',
    icon: 'bg-bad-100 text-bad-700 dark:bg-bad-950 dark:text-bad-300',
    node: <CircleAlert />
  },
  info: {
    shell: 'border-line bg-surface',
    icon: 'bg-accent-soft text-brand-700 dark:text-brand-200',
    node: <Info />
  }
}

let nextToastId = 0

export function ToastProvider({ children }: ToastProviderProps): ReactElement {
  const [toasts, setToasts] = useState<ToastRecord[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback((options: ToastOptions) => {
    const record: ToastRecord = { ...options, id: nextToastId++ }
    setToasts((current) => [...current, record].slice(-MAX_VISIBLE))
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[70] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:items-end"
      >
        {toasts.map((item) => (
          <ToastItem key={item.id} toast={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a <ToastProvider>')
  return context
}

function ToastItem({
  toast,
  onDismiss
}: {
  toast: ToastRecord
  onDismiss: (id: number) => void
}): ReactElement {
  const variant = toast.variant ?? 'info'
  const style = variants[variant]
  const remaining = useRef(DURATIONS[variant])
  const startedAt = useRef(0)
  const timer = useRef<number | null>(null)

  const stop = useCallback(() => {
    if (timer.current === null) return
    window.clearTimeout(timer.current)
    timer.current = null
    remaining.current = Math.max(800, remaining.current - (Date.now() - startedAt.current))
  }, [])

  const start = useCallback(() => {
    if (timer.current !== null) return
    startedAt.current = Date.now()
    timer.current = window.setTimeout(() => onDismiss(toast.id), remaining.current)
  }, [onDismiss, toast.id])

  useEffect(() => {
    start()
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [start])

  return (
    <div
      onMouseEnter={stop}
      onMouseLeave={start}
      onFocus={stop}
      onBlur={start}
      className={cn(
        'animate-rise pointer-events-auto flex w-full items-start gap-3 rounded-card border p-3 shadow-pop sm:w-[22rem]',
        style.shell
      )}
    >
      <span
        aria-hidden="true"
        className={cn('grid size-7 shrink-0 place-items-center rounded-full [&_svg]:size-4', style.icon)}
      >
        {style.node}
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-semibold leading-snug text-fg">{toast.title}</p>
        {toast.description ? (
          <p className="mt-1 break-words text-xs leading-relaxed text-fg-muted">
            {toast.description}
          </p>
        ) : null}
      </div>
      <IconButton label="Dismiss notification" size="sm" onClick={() => onDismiss(toast.id)}>
        <X />
      </IconButton>
    </div>
  )
}
